import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { CourseDetailEntity, CourseEntity, CoursePayload } from 'src/entities/Course.entity';
import { useCreateCourseMutation } from 'src/hooks/useCourseMutations';

import { nextTripOptions, tripLabel } from './courseMasterUtils';
import TripGroupAlignDialog from './TripGroupAlignDialog';
import { useCourseOptions } from './useCourseOptions';
import { useCourseSaveFlow } from './useCourseSaveFlow';

type Props = { open: boolean; courses: CourseEntity[]; onClose: () => void; onCreated: (course: CourseDetailEntity) => void };
type Form = {
  code: string;
  name: string;
  partnerCompanyId: number | '';
  truckId: number | '';
  driverId: number | '';
  tripNo: number;
  firstTripCourseId: number | '';
};
const EMPTY: Form = { code: '', name: '', partnerCompanyId: '', truckId: '', driverId: '', tripNo: 1, firstTripCourseId: '' };
const toId = (value: number | '') => (value === '' ? null : value);
const toSelectValue = (raw: string): number | '' => (raw === '' ? '' : Number(raw));

/** 新規コース。所属ダイヤはここでは受けず（POST /courses は所属を持たない）、ダイヤ詳細の「コースを追加」で入れる */
export default function NewCourseDialog({ open, courses, onClose, onCreated }: Props) {
  const [form, setForm] = useState<Form>(EMPTY);
  const { trucks, drivers, partners } = useCourseOptions();
  const { mutateAsync } = useCreateCourseMutation();
  const send = useCallback((data: CoursePayload) => mutateAsync(data), [mutateAsync]);
  const saved = useCallback(
    (course: CourseDetailEntity) => {
      setForm(EMPTY);
      onCreated(course);
    },
    [onCreated],
  );
  const flow = useCourseSaveFlow(send, saved);
  // 紐付け先は deleted でない 1 便すべて（API 仕様）
  const firstTrips = courses.filter((c) => c.tripNo === 1);
  const needsFirst = form.tripNo >= 2;
  const invalid = !form.code.trim() || (needsFirst && form.firstTripCourseId === '');
  const submit = () => {
    void flow.run({
      code: form.code.trim(),
      name: form.name.trim() || null,
      trip_no: form.tripNo,
      first_trip_course_id: needsFirst ? toId(form.firstTripCourseId) : null,
      default_truck_id: toId(form.truckId),
      default_driver_id: toId(form.driverId),
      partner_company_id: toId(form.partnerCompanyId),
    });
  };
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>コースを追加</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
          {flow.alert && (
            <Alert severity="error" onClose={flow.clearAlert}>
              {flow.alert}
            </Alert>
          )}
          <TextField size="small" label="コース番号" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <TextField size="small" label="コース名" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField
            select
            size="small"
            label="通常運送会社"
            value={form.partnerCompanyId}
            onChange={(e) => setForm({ ...form, partnerCompanyId: toSelectValue(e.target.value) })}
          >
            <MenuItem value="">未設定</MenuItem>
            {partners.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="通常車両"
            value={form.truckId}
            onChange={(e) => setForm({ ...form, truckId: toSelectValue(e.target.value) })}
          >
            <MenuItem value="">未設定</MenuItem>
            {trucks.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="通常ドライバー"
            value={form.driverId}
            onChange={(e) => setForm({ ...form, driverId: toSelectValue(e.target.value) })}
          >
            <MenuItem value="">未設定</MenuItem>
            {drivers.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="便区分"
            value={form.tripNo}
            onChange={(e) => {
              const tripNo = Number(e.target.value);
              setForm({ ...form, tripNo, firstTripCourseId: tripNo === 1 ? '' : form.firstTripCourseId });
            }}
          >
            {nextTripOptions(courses).map((n) => (
              <MenuItem key={n} value={n}>
                {tripLabel(n)}
              </MenuItem>
            ))}
          </TextField>
          {needsFirst && (
            <TextField
              select
              required
              size="small"
              label="紐付け先の1便コース"
              value={form.firstTripCourseId}
              onChange={(e) => setForm({ ...form, firstTripCourseId: Number(e.target.value) })}
            >
              {firstTrips.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code}
                  {c.name ? ` ${c.name}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="contained" disableElevation disabled={invalid || flow.pending} onClick={submit}>
            登録する
          </Button>
        </DialogActions>
      </Dialog>
      <TripGroupAlignDialog
        mismatch={flow.mismatch?.error ?? null}
        selfLabel={`${form.code.trim()} ${tripLabel(form.tripNo)}`}
        onChoose={flow.resolveMismatch}
        onClose={flow.dismissMismatch}
      />
    </>
  );
}
